/**
 * Conteneur principal pour le formulaire référent
 * Version refactorisée et simplifiée de ReferentForm.tsx
 * Phase 7 - Écran Acceptation Référent (UX)
 */

import { useState, useEffect, Suspense, useRef } from 'react';

import { useReferentForm } from '@/hooks/useReferentForm';
import { useReferentBranding } from '@/hooks/useReferentBranding';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/productionLogger';
import { BrandedButton, BrandedSpinner } from '@/components/shared/ui';

// Plus besoin des composants de loading states complexes
import { ReferentFormNavigation } from './ReferentFormNavigation';
import { stepTitles } from '@/utils/referentFormNavigation';

// FIX: Import direct des composants pour éviter les erreurs de chunks React
import { ContextualInfoStep } from './ContextualInfoStep';
import { SignatureStep } from './SignatureStep';
import { CollaborationContextStep } from './CollaborationContextStep';
import { DynamicRatingStep } from './DynamicRatingStep';
import { CriticalQuestionsStep } from './CriticalQuestionsStep';
import { VoiceRecordingStep } from './VoiceRecordingStep';
import { ConfirmationStep } from './ConfirmationStep';
import { SuccessStep } from './SuccessStep';
import { AlreadySubmittedStep } from './AlreadySubmittedStep';

// Phase 7 - Nouveaux composants d'acceptation/refus
import { ReferentDecisionForm } from './ReferentDecisionForm';
import { ReferentRefusalConfirmationScreen } from './ReferentRefusalConfirmationScreen';

// TOTAL_STEPS supprimé - géré maintenant dans useReferentForm

export const ReferentFormContainer = () => {
  const [error, setError] = useState<string | null>(null);
  const signatureStepRef = useRef<any>(null);

  // Phase 7 - Détection si l'utilisateur veut refuser directement depuis l'email
  const [wantsToDecline, setWantsToDecline] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('action') === 'decline';
    }
    return false;
  });

  // Phase 7 - État d'acceptation du référent
  const [hasAccepted, setHasAccepted] = useState<boolean | null>(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        const saved = sessionStorage.getItem(`referent_acceptance_${token}`);
        if (saved === 'true') return true;
        if (saved === 'false') return false;
      }
    }
    return null;
  });

  // Hooks principaux
  const {
    currentStep,
    setCurrentStep,
    formData,
    updateFormData,
    campaign,
    referent,
    evaluationCriteria,
    isSubmitting,
    loadReferentData,
    submitResponse,
    handleNext,
    handlePrevious,
    isLastStep,
    canGoNext,
    canGoPrevious,
    canSubmit,
    // Fingerprinting
    fingerprintConsent,
    setFingerprintConsent,

    isGenerating,
    fingerprintError,
  } = useReferentForm();

  const { branding } = useReferentBranding(campaign?.id);

  // État de chargement consolidé - simplifié sans contexte
  const [isLoading, setIsLoading] = useState(true);
  const loadingMessage = isLoading ? "Chargement des données..." : "";

  // Navigation gérée maintenant dans useReferentForm

  // Gestion du chargement des données - OPTIMISÉ avec chargement parallèle
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Charger les données directement si on n'a pas encore de campagne et référent
        if (!campaign && !referent) {
          logger.debug('Début chargement parallèle des données référent');
          
          // OPTIMISATION: Chargement parallèle au lieu de séquentiel
          // Gain attendu: ~40% de réduction du temps de chargement
          await loadReferentData();
          
          // Note: Le branding sera chargé en parallèle par useReferentBranding
          // une fois que campaign.id sera disponible
        }

        setIsLoading(false);
      } catch (err: any) {
        logger.error('Erreur dans le composant ReferentForm', err);
        setError(err.message || 'Erreur lors du chargement des données');
        setIsLoading(false);
      }
    };

    loadData();
  }, [campaign, referent, loadReferentData]);

  // Gestion des erreurs - simplifié
  const handleClearError = () => {
    setError(null);
  };

  // Gestion de la soumission
  const handleSubmit = async () => {
    try {
      logger.referentAction('soumission_formulaire', referent?.id, campaign?.id);
      const success = await submitResponse();
      
      if (success) {
        // Navigation vers écran de succès ou étape finale
        setCurrentStep(8); // Étape de succès
        // Scroll vers le haut pour l'écran de succès
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err: any) {
      logger.error('Erreur soumission formulaire référent', err);
      setError(err.message || 'Erreur lors de la soumission');
    }
  };

  // Gestion de la signature
  const handleSignatureClick = async () => {
    if (signatureStepRef.current && signatureStepRef.current.handleSign) {
      await signatureStepRef.current.handleSign();
    }
  };

  // Navigation directe après signature (bypass validation)
  const handleSignatureComplete = () => {
    logger.debug('Navigation directe après signature - Étape 2 → 3');
    logger.debug('FormData avant navigation', { formData });
    
    // Navigation directe vers étape 3 sans validation
    setCurrentStep(3);
    
    // Scroll vers le haut lors du changement d'étape
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    logger.debug('Navigation vers étape 3 effectuée');
  };

  // Affichage des états de chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BrandedSpinner branding={branding} size="lg" className="mx-auto mb-4" />
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // Affichage des erreurs
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <BrandedButton
            onClick={handleClearError}
            className="px-4 py-2 rounded"
            branding={branding}
          >
            Réessayer
          </BrandedButton>
        </div>
      </div>
    );
  }

  // Vérification des données requises
  if (!campaign || !referent || !evaluationCriteria) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-yellow-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Données manquantes</h3>
          <p className="text-gray-600">Impossible d'afficher le formulaire. Veuillez réessayer.</p>
        </div>
      </div>
    );
  }

  // Phase 7 - Logique d'affichage avec acceptation/refus

  // 1. Référent a refusé - TRIPLE GARDE-FOU (status + timestamp + session)
  if (referent?.status === 'Refusé' || referent?.refused_at || hasAccepted === false) {
    return (
      <ReferentRefusalConfirmationScreen
        candidateName={campaign.candidate_name}
        refusalReason={referent?.refusal_reason}
        branding={branding}
      />
    );
  }

  // 2. Référent a déjà complété
  if (referent?.status === 'Complété') {
    return <AlreadySubmittedStep candidateName={campaign?.candidate_name} branding={branding} />;
  }

  // 3. Référent n'a pas encore accepté
  if (hasAccepted === null) {
    return (
      <ReferentDecisionForm
        referent={referent}
        campaign={campaign}
        branding={branding}
        initialShowRefusalForm={wantsToDecline}
        onAccept={() => {
          if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            if (token) {
              sessionStorage.setItem(`referent_acceptance_${token}`, 'true');
            }
          }
          setHasAccepted(true);
          setWantsToDecline(false);
        }}
        onRefuse={async (reason: string) => {
          try {
            logger.referentAction('refus_referent', referent.id, { reason });

            // ✅ Appel à l'edge function avec gestion d'erreur
            const { data, error } = await supabase.functions.invoke('submit-referent-response', {
              body: {
                referent_id: referent.id,
                response_type: 'refuse',
                refusal_reason: reason
              }
            });

            if (error) {
              logger.error('Erreur Edge Function refus référent', error, { referentId: referent.id });
              // ❌ NE PAS mettre à jour l'état local si l'appel a échoué
              setError('Une erreur est survenue lors de l\'enregistrement de votre refus. Veuillez réessayer.');
              return;
            }

            // ✅ Succès : mettre à jour l'état local
            logger.info('Refus référent enregistré avec succès', { referentId: referent.id });
            
            if (typeof window !== 'undefined') {
              const urlParams = new URLSearchParams(window.location.search);
              const token = urlParams.get('token');
              if (token) {
                sessionStorage.setItem(`referent_acceptance_${token}`, 'false');
              }
            }
            setHasAccepted(false);
            
          } catch (err: any) {
            logger.error('Erreur inattendue lors du refus référent', err);
            setError('Une erreur est survenue. Veuillez réessayer.');
          }
        }}
        isSubmitting={isSubmitting}
      />
    );
  }

  // 4. Écran de succès en pleine page
  if (currentStep === 8) {
    return <SuccessStep candidateName={campaign?.candidate_name} branding={branding} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 font-['Bricolage_Grotesque']">
      <div className="max-w-4xl mx-auto px-4">
        {/* En-tête avec logo Getproof et badge */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <img
            src={(branding?.enabled && branding?.logoUrl) ? branding.logoUrl : "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"}
            alt={(branding?.enabled && branding?.logoUrl) ? "Logo Entreprise" : "Getproof Logo"}
            className="h-10 sm:h-12"
          />
          <div
            className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
            style={{
              outlineColor: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#4ECDC4',
              color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#4ECDC4'
            }}
          >
            <div className="text-xs sm:text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight text-center">
              Formulaire de référence
            </div>
          </div>
        </div>

        {/* Titre candidat */}
        <div className="text-center mb-8">
          <p className="text-gray-600 text-base sm:text-lg font-normal font-['Bricolage_Grotesque']">
            Candidat: {campaign.candidate_name} - {campaign.target_position}
          </p>
        </div>

        {/* Contenu principal */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-8">
          {/* Titre dynamique de l'étape - sauf pour la signature qui a son propre titre */}
          {currentStep !== 2 && (
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl font-medium font-['Bricolage_Grotesque'] text-gray-800 leading-7">
                {stepTitles[currentStep - 1]}
              </h2>
            </div>
          )}

          <Suspense fallback={
            <div className="flex justify-center py-8">
              <BrandedSpinner branding={branding} size="lg" />
            </div>
          }>
            {renderCurrentStep()}
          </Suspense>

          {/* Navigation - Masquée sur l'étape de succès */}
          {currentStep !== 8 && (
            <ReferentFormNavigation
              currentStep={currentStep}
              totalSteps={7}
              canGoNext={canGoNext}
              canGoPrevious={canGoPrevious}
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              isLastStep={isLastStep}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onSubmit={handleSubmit}
              branding={branding}
              onSignatureClick={handleSignatureClick}
              isSignaturePending={signatureStepRef.current?.isSignaturePending}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Fonction pour rendre l'étape courante
  function renderCurrentStep() {
    const commonProps = {
      formData,
      updateFormData,
      campaign,
      referent,
      evaluationCriteria,
      branding
    };

    switch (currentStep) {
      case 1:
        return <ContextualInfoStep {...commonProps} />;
      case 2:
        return <SignatureStep
          ref={signatureStepRef}
          referentData={{
            id: referent.id,
            name: referent.name,
            email: referent.email
          }}
          campaignId={campaign.id}
          updateFormData={updateFormData}
          branding={branding}
          onSignatureComplete={handleSignatureComplete}
        />;
      case 3:
        return <CollaborationContextStep 
          {...commonProps} 
          // Pré-remplissage avec les données candidat si disponibles
          candidateStartDate={referent?.candidate_collaboration_start_date}
          candidateEndDate={referent?.candidate_collaboration_end_date}
          candidateStillWorkingTogether={referent?.candidate_still_working_together}
        />;
      case 4:
        return <DynamicRatingStep
          {...commonProps}
          criteria={evaluationCriteria}
          initialData={formData.ratings}
        />;
      case 5:
        return <CriticalQuestionsStep {...commonProps} />;
      case 6:
        return <VoiceRecordingStep {...commonProps} />;
      case 7:
        return <ConfirmationStep
          {...commonProps}
          candidateName={campaign?.candidate_name}
          fingerprintConsent={fingerprintConsent}
          setFingerprintConsent={setFingerprintConsent}
          isGeneratingFingerprint={isGenerating}
          fingerprintError={fingerprintError}
        />;
      case 8:
        return <SuccessStep
          candidateName={campaign?.candidate_name}
          branding={branding}
        />;
      default:
        return <div>Étape non trouvée</div>;
    }
  }
};